import React from "react";
import PropTypes from "prop-types";
import {
  Divider,
  Typography,
  Toolbar,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  Paper,
  TableContainer,
  TableBody,
  Stack,
  Pagination,
} from "@mui/material";

import { alpha } from "@mui/material/styles";
import SearchClients from "../../Clients/SearchClients";
import SkeletonTable from "../../General/SkeletonTable";

const headCells = [
  {
    id: "legal_name",
    numeric: false,
    disablePadding: true,
    label: "Razón social",
  },
  {
    id: "tax_id",
    numeric: true,
    disablePadding: false,
    label: "RFC",
  },

  {
    id: "tax_system",
    numeric: true,
    disablePadding: false,
    label: "Regimen",
  },
];

function EnhancedTableHead(props) {
  const { onSelectAllClick, numSelected, rowCount } = props;

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount - 1}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = (props) => {
  const { numSelected } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} seleccionadas
        </Typography>
      ) : (
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Facturas
        </Typography>
      )}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const CheckListMassBillingBills = ({
  clients,
  setSelectedRows,
  selectedRows,
  triggerAxiosPaginate,
  search,
  setSearch,
  page,
  totalPages,
  countClients,
  loading,
}) => {
  const handleChangePage = (event, page) => {
    triggerAxiosPaginate(page, search);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = clients
        ?.filter((option) => option.tax_id !== "XAXX010101000")
        .map((n) => n);

      setSelectedRows(newSelected);
      return;
    }
    setSelectedRows([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selectedRows.indexOf(name);

    let newSelected = [];
    if (name.tax_id !== "XAXX010101000") {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedRows, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedRows.slice(1));
      } else if (selectedIndex === selectedRows.length - 1) {
        newSelected = newSelected.concat(selectedRows.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedRows.slice(0, selectedIndex),
          selectedRows.slice(selectedIndex + 1)
        );
      }

      setSelectedRows(newSelected);
    }
  };

  const isSelected = (name) => selectedRows.indexOf(name) !== -1;

  return (
    <>
      <Typography
        variant="h6"
        fontWeight="600"
        marginBottom={"1%"}
        marginTop={"2%"}
      >
        Clientes
      </Typography>
      <Divider style={{ marginBottom: "20px" }} />
      <SearchClients
        triggerAxiosPaginate={triggerAxiosPaginate}
        search={search}
        setSearch={setSearch}
      />
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <EnhancedTableToolbar numSelected={selectedRows.length} />
          <TableContainer>
            <Table
              sx={{ minWidth: 590 }}
              aria-labelledby="tableTitle"
              size={"small"}
            >
              <EnhancedTableHead
                numSelected={selectedRows.length}
                onSelectAllClick={handleSelectAllClick}
                rowCount={countClients}
              />
              <TableBody>
                {loading ? (
                  <SkeletonTable />
                ) : (
                  clients?.map((row, index) => {
                    const isItemSelected = isSelected(row);

                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        onClick={(event) => handleClick(event, row)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.id}
                        selected={isItemSelected}
                        disabled={row.tax_id === "XAXX010101000"}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            disabled={row.tax_id === "XAXX010101000"}
                            inputProps={{
                              "aria-labelledby": labelId,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          padding="none"
                        >
                          {row?.tax_id}
                        </TableCell>
                        <TableCell align="right">{row?.legal_name}</TableCell>

                        <TableCell align="right">{row?.tax_system}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack spacing={2} style={{ float: "right" }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              variant="outlined"
              shape="rounded"
            />
          </Stack>
        </Paper>
      </Box>
    </>
  );
};

export default CheckListMassBillingBills;
